import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { apiClient } from '../../api/client'
import { useAssetTypes } from '../../hooks/useQueries'
import { useLocale } from '../../contexts/LocaleContext'
import { getLocalizedName } from '../../lib/localization'

/** The asset types a column can be, stair cages and lift shafts first. */
export const COLUMN_TYPE_CODES = ['stairwell', 'elevator_shaft', 'fire_riser', 'elec_riser'] as const

/** The relation a column declares the levels it crosses with (spec 2026-10-25-21). */
export const CROSSES_LEVEL_RELATION = 'crosses_level'

/**
 * « Ajouter une colonne » from the stacking plan's « Modifier » menu: a stair
 * cage, a lift shaft or a riser, from one level to another. It creates the
 * column under the building and one `crosses_level` link per level, so the
 * column shows in the plan's lane without any floor plan drawn.
 *
 * `floors` is the plan's rows, top floor first.
 */
export function AddColumnDialog({ org, buildingUid, floors, onClose }: {
    org: string
    buildingUid: string
    floors: Array<{ uid: string; label: string }>
    onClose: () => void
}) {
    const { t } = useTranslation('assets')
    const { locale } = useLocale()
    const queryClient = useQueryClient()
    const { data: assetTypes = [] } = useAssetTypes(org)
    const kinds = useMemo(
        () => COLUMN_TYPE_CODES
            .map((code) => assetTypes.find((at) => at.code === code))
            .filter((at): at is NonNullable<typeof at> => Boolean(at)),
        [assetTypes],
    )

    const [name, setName] = useState('')
    const [kind, setKind] = useState<string>(COLUMN_TYPE_CODES[0])
    const [top, setTop] = useState(floors[0]?.uid ?? '')
    const [bottom, setBottom] = useState(floors[floors.length - 1]?.uid ?? '')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const topIndex = floors.findIndex((f) => f.uid === top)
    const bottomIndex = floors.findIndex((f) => f.uid === bottom)
    const crossed = topIndex >= 0 && bottomIndex >= topIndex ? floors.slice(topIndex, bottomIndex + 1) : []
    const kindType = kinds.find((k) => k.code === kind)
    const valid = name.trim() !== '' && !!kindType && crossed.length > 0

    const submit = async () => {
        if (!valid || !kindType) return
        setSaving(true)
        setError(null)
        try {
            const { data: created, error: createError } = await apiClient.POST('/api/v1/organizations/{orgCode}/assets', {
                params: { path: { orgCode: org } },
                body: { name: name.trim(), asset_type_uid: kindType.uid, parent_asset_uid: buildingUid },
            })
            if (createError || !created?.uid) throw createError ?? new Error('create failed')
            try {
                for (const floor of crossed) {
                    const { error: relError } = await apiClient.POST('/api/v1/organizations/{orgCode}/assets/{assetUid}/relations', {
                        params: { path: { orgCode: org, assetUid: created.uid } },
                        body: { relation_type: CROSSES_LEVEL_RELATION, target_asset_uid: floor.uid },
                    } as never)
                    if (relError) throw relError
                }
            } catch (relationError) {
                // All or nothing: a column without its levels would be an
                // orphan, and a retry would create a second one.
                await apiClient.DELETE('/api/v1/organizations/{orgCode}/assets/{assetUid}', {
                    params: { path: { orgCode: org, assetUid: created.uid } },
                } as never)
                throw relationError
            }
            await queryClient.invalidateQueries({
                predicate: (q) => Array.isArray(q.queryKey) && q.queryKey.some((k) => typeof k === 'string' && (k.includes('/plan/risers') || k.includes('/assets'))),
            })
            onClose()
        } catch (e) {
            setError(e instanceof Error ? e.message : t('stacking_plan.add_column_error'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
            <DialogContent className="sm:max-w-md" data-testid="add-column-dialog">
                <DialogHeader>
                    <DialogTitle>{t('stacking_plan.add_column')}</DialogTitle>
                    <DialogDescription>{t('stacking_plan.add_column_description')}</DialogDescription>
                </DialogHeader>
                <div className="grid gap-3">
                    <div className="grid gap-1.5">
                        <Label htmlFor="column-kind">{t('stacking_plan.add_column_kind')}</Label>
                        <Select value={kind} onValueChange={setKind}>
                            <SelectTrigger id="column-kind" data-testid="add-column-kind"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {kinds.map((k) => (
                                    <SelectItem key={k.code} value={k.code} data-testid={`add-column-kind-${k.code}`}>
                                        {getLocalizedName(k.name as Record<string, string>, locale) || k.code}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="column-name">{t('stacking_plan.add_column_name')}</Label>
                        <Input id="column-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('stacking_plan.add_column_name_placeholder')} data-testid="add-column-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-1.5">
                            <Label>{t('stacking_plan.add_column_top')}</Label>
                            <Select value={top} onValueChange={setTop}>
                                <SelectTrigger data-testid="add-column-top"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {floors.map((f) => <SelectItem key={f.uid} value={f.uid}>{f.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-1.5">
                            <Label>{t('stacking_plan.add_column_bottom')}</Label>
                            <Select value={bottom} onValueChange={setBottom}>
                                <SelectTrigger data-testid="add-column-bottom"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {floors.map((f) => <SelectItem key={f.uid} value={f.uid}>{f.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground" data-testid="add-column-summary">
                        {crossed.length > 0
                            ? t('stacking_plan.add_column_levels', { count: crossed.length })
                            : t('stacking_plan.add_column_levels_invalid')}
                    </p>
                    {error && <p className="text-xs text-destructive" data-testid="add-column-error">{error}</p>}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t('common:actions.cancel', 'Cancel')}</Button>
                    <Button onClick={submit} disabled={!valid || saving} data-testid="add-column-submit">
                        {t('stacking_plan.add_column_submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
